import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { PROPERTY_TYPES, EMIRATES } from '@/lib/constants';

const SOURCES_QA = ['WhatsApp', 'Walk-in', 'Referral', 'Call', 'Other'];
const PRIORITIES_QA = [
    { v: 'Hot', label: 'Hot', color: '#EF4444' },
    { v: 'Warm', label: 'Warm', color: '#F59E0B' },
    { v: 'Cold', label: 'Cold', color: '#3B82F6' },
];

function formatUAEPhone(input) {
    if (!input) return input;
    const digits = input.replace(/[^0-9+]/g, '');
    if (!digits) return digits;
    if (digits.startsWith('+971')) return '+971 ' + digits.slice(4);
    if (digits.startsWith('971')) return '+971 ' + digits.slice(3);
    if (digits.startsWith('0')) return '+971 ' + digits.slice(1);
    if (!digits.startsWith('+')) return '+971 ' + digits;
    return digits;
}

export default function QuickAddModal({ open, onClose }) {
    const { profile } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('+971 ');
    const [source, setSource] = useState('WhatsApp');
    const [priority, setPriority] = useState('Warm');
    const [propertyType, setPropertyType] = useState('');
    const [emirate, setEmirate] = useState('');
    const [budget, setBudget] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data, error } = await supabase.from('pipeline_contacts').insert({
                organization_id: profile.organization_id,
                created_by: profile.id,
                assigned_to: profile.id,
                pipeline_stage: 'New',
                full_name: name,
                phone: phone.replace(/\s+/g, ''),
                source,
                priority,
                property_type: propertyType || null,
                emirate_preference: emirate || null,
                budget_aed: budget ? Number(budget) : null,
                notes: notes || null,
            }).select().single();
            if (error) throw error;
            await supabase.from('pipeline_activities').insert({
                contact_id: data.id, organization_id: profile.organization_id, agent_id: profile.id,
                activity_type: 'note', description: `Contact added by ${profile.full_name || 'user'}`,
            });
            toast.success('Lead added');
            onClose();
            setName(''); setPhone('+971 '); setNotes(''); setBudget(''); setPropertyType(''); setEmirate('');
            navigate(`/pipeline/${data.id}`);
        } catch (err) {
            toast.error(err.message || 'Failed to add lead');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="quick-add-modal">
                <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between rounded-t-xl">
                    <div>
                        <h2 className="text-xl font-bold text-aks-text">Quick add lead</h2>
                        <p className="text-[12px] text-gray-500">30-second capture — you can add details after</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-aks-text"><X className="h-5 w-5"/></button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <label className="text-[12px] font-semibold text-aks-text">Full name *</label>
                        <input autoFocus required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full h-12 px-3 rounded-md border border-gray-300 focus:border-aks-gold aks-focus-ring outline-none text-[15px]" data-testid="qa-name" />
                    </div>
                    <div>
                        <label className="text-[12px] font-semibold text-aks-text">Phone *</label>
                        <input required value={phone} onChange={(e) => setPhone(formatUAEPhone(e.target.value))} className="mt-1 w-full h-12 px-3 rounded-md border border-gray-300 focus:border-aks-gold aks-focus-ring outline-none text-[15px] font-mono" data-testid="qa-phone" />
                    </div>
                    <div>
                        <label className="text-[12px] font-semibold text-aks-text">Source *</label>
                        <div className="mt-1.5 flex flex-wrap gap-2">
                            {SOURCES_QA.map((s) => (
                                <button type="button" key={s} onClick={() => setSource(s)} className={`h-10 px-4 rounded-md border text-[13px] font-semibold transition ${source === s ? 'border-aks-gold bg-aks-gold-soft text-aks-navy' : 'border-gray-300 text-gray-600 hover:border-aks-gold/60'}`} data-testid={`qa-src-${s}`}>{s}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-[12px] font-semibold text-aks-text">Priority *</label>
                        <div className="mt-1.5 grid grid-cols-3 gap-2">
                            {PRIORITIES_QA.map((p) => (
                                <button type="button" key={p.v} onClick={() => setPriority(p.v)} className={`h-11 rounded-md border text-[13px] font-semibold transition flex items-center justify-center gap-2 ${priority === p.v ? 'border-aks-gold bg-aks-gold-soft text-aks-navy' : 'border-gray-300 text-gray-600 hover:border-aks-gold/60'}`} data-testid={`qa-pri-${p.v}`}>
                                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} /> {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[12px] font-medium text-gray-600">Property type</label>
                            <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px] capitalize">
                                <option value="">—</option>
                                {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[12px] font-medium text-gray-600">Emirate</label>
                            <select value={emirate} onChange={(e) => setEmirate(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px]">
                                <option value="">—</option>
                                {EMIRATES.map((e) => <option key={e} value={e}>{e}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-[12px] font-medium text-gray-600">Budget (AED)</label>
                        <input type="number" value={budget} onChange={(e) => setBudget(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 text-[14px]" placeholder="e.g. 1500000" />
                    </div>
                    <div>
                        <label className="text-[12px] font-medium text-gray-600">Notes</label>
                        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full p-3 rounded-md border border-gray-300 text-[14px]" placeholder="Any context…" />
                    </div>
                </div>
                <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 rounded-b-xl">
                    <button type="submit" disabled={loading || !name || !phone} className="w-full h-12 rounded-md bg-aks-gold text-aks-navy font-bold text-[15px] hover:brightness-105 disabled:opacity-60 transition aks-focus-ring inline-flex items-center justify-center gap-2" data-testid="qa-submit">
                        <Plus className="h-5 w-5" /> {loading ? 'Adding…' : 'Add lead'}
                    </button>
                </div>
            </form>
        </div>
    );
}
